import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, Package, CheckCircle, Download, ExternalLink, AlertTriangle, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function MKDeliveryTracking() {
  const handleAcknowledge = () => {
    toast.success('Delivery acknowledged successfully! Thank you.');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Truck className="w-7 h-7 text-[#5B3F8A]" />
          Delivery Tracking & Acknowledgement
        </h1>
      </div>

      {/* Tracking Info */}
      <Card className="bg-[#5B3F8A]/5 border-[#5B3F8A]/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-500">Delivery ID</p>
              <p className="font-mono font-semibold">DO-MK-BND-2026-001</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Courier Tracking</p>
              <p className="font-mono font-semibold">OT-MK-DISPATCH-BND-2026</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Stepper */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <span className="text-xs mt-2 font-medium">Order Confirmed</span>
              <span className="text-[10px] text-slate-500">Apr 2026</span>
            </div>
            <div className="flex-1 h-1 bg-emerald-500 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <span className="text-xs mt-2 font-medium">Processing</span>
              <span className="text-[10px] text-slate-500">May–Aug 2026</span>
            </div>
            <div className="flex-1 h-1 bg-emerald-500 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <span className="text-xs mt-2 font-medium">Dispatched</span>
              <span className="text-[10px] text-slate-500">25 Sep 2026</span>
            </div>
            <div className="flex-1 h-1 bg-emerald-500 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center animate-pulse">
                <Truck className="w-6 h-6" />
              </div>
              <span className="text-xs mt-2 font-medium text-amber-600">In Transit</span>
              <span className="text-[10px] text-amber-600">ETA 30 Sep</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                <Package className="w-6 h-6 text-slate-400" />
              </div>
              <span className="text-xs mt-2 text-slate-400">Delivered</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-slate-400" />
              </div>
              <span className="text-xs mt-2 text-slate-400">Acknowledged</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Detail */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">📦 Your 20 books are on their way!</h3>
              <div className="mt-2 text-sm text-slate-600 space-y-1">
                <p><strong>Dispatched by:</strong> OpenTrolley → Civica → Your school</p>
                <p><strong>Tracking reference:</strong> OT-MK-DISPATCH-BND-2026</p>
                <p><strong>Last update:</strong> "Package in transit — Singapore logistics network | 27 Sep 2026 14:32"</p>
                <p><strong>Estimated delivery:</strong> 30 Sep 2026</p>
              </div>
              <Button variant="outline" size="sm" className="mt-3">
                <ExternalLink className="w-4 h-4 mr-2" />
                Track on courier website
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Acknowledgement Button (disabled until delivered) */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-slate-400" />
            <div className="flex-1">
              <p className="font-medium text-slate-600">Acknowledge Delivery</p>
              <p className="text-sm text-slate-500">Available once delivery is confirmed</p>
            </div>
            <Button disabled className="bg-slate-300">
              <CheckCircle className="w-4 h-4 mr-2" />
              Acknowledge Receipt
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* What happens when delivered */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            When your delivery arrives...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside text-sm text-slate-700 space-y-2">
            <li>Check each book against the list (all 20 titles)</li>
            <li>Note any issues (damaged covers, missing books, wrong titles)</li>
            <li>Click "Acknowledge Receipt" and confirm condition of each book</li>
            <li>Report any discrepancies — Civica will send replacements</li>
          </ol>
          <p className="text-xs text-amber-700 mt-4">
            ⏰ Please acknowledge within 5 working days of delivery
          </p>
        </CardContent>
      </Card>

      {/* Help Contact */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-3">Need Help?</h3>
          <p className="text-sm text-slate-600 mb-3">
            Delivery running late? Contact Civica:
          </p>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-[#5B3F8A]" />
              <span>slfcollections@civica.com.sg</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-[#5B3F8A]" />
              <span>6225 8800</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Mon–Fri 8am–6.30pm</p>
        </CardContent>
      </Card>
    </div>
  );
}
